/**
 * HeightSamplerNode - Samples terrain height at given (x, z) positions
 *
 * Takes a heightmap (grid of Y values + bounds) and arrays of X/Z positions,
 * outputs an array of Y heights using bilinear interpolation.
 * Connect between a position generator (WangTiler/Scatter) and a Repeater
 * to place objects on terrain.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { Heightmap } from '../../threejs/nodes/TerrainNode';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const heightSamplerEndpoints = {
  ...params({
    offset: { value: 0.05, min: -5, max: 5, step: 0.01, label: 'Y Offset' },
  }),
  ...internals({
    heightmap: { dataType: 'array', isInput: true, label: 'Heightmap' },
    posX: { dataType: 'array', isInput: true, label: 'X' },
    posZ: { dataType: 'array', isInput: true, label: 'Z' },
    posY: { dataType: 'array', isOutput: true, label: 'Y' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Bilinear interpolation
// ─────────────────────────────────────────────────────────────

function sampleHeightmap(hm: Heightmap, x: number, z: number): number {
  // Map world coords to grid coords
  const gx = ((x - hm.minX) / (hm.maxX - hm.minX)) * (hm.cols - 1);
  const gz = ((z - hm.minZ) / (hm.maxZ - hm.minZ)) * (hm.rows - 1);

  // Clamp to grid bounds
  const cx = Math.max(0, Math.min(hm.cols - 1, gx));
  const cz = Math.max(0, Math.min(hm.rows - 1, gz));

  const ix = Math.floor(cx);
  const iz = Math.floor(cz);
  const fx = cx - ix;
  const fz = cz - iz;

  // Clamp indices for right/bottom neighbors
  const ix1 = Math.min(ix + 1, hm.cols - 1);
  const iz1 = Math.min(iz + 1, hm.rows - 1);

  // Sample 4 corners
  const v00 = hm.data[iz  * hm.cols + ix];
  const v10 = hm.data[iz  * hm.cols + ix1];
  const v01 = hm.data[iz1 * hm.cols + ix];
  const v11 = hm.data[iz1 * hm.cols + ix1];

  // Bilinear interpolation
  return v00 * (1 - fx) * (1 - fz) +
         v10 * fx * (1 - fz) +
         v01 * (1 - fx) * fz +
         v11 * fx * fz;
}

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class HeightSamplerNode extends BaseNode<typeof heightSamplerEndpoints> {
  private cachedHeightmap: Heightmap | null = null;
  private cachedPosX: number[] | null = null;
  private cachedPosZ: number[] | null = null;

  onInternalChanged(payload: Payload, endpointId: string): void {
    const value = payload.value;
    if (endpointId === 'heightmap') {
      this.cachedHeightmap = value as Heightmap;
    } else if (endpointId === 'posX') {
      this.cachedPosX = value as number[];
    } else if (endpointId === 'posZ') {
      this.cachedPosZ = value as number[];
    }
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    if (!this.cachedHeightmap || !this.cachedPosX || !this.cachedPosZ) return;

    const hm = this.cachedHeightmap;
    const xs = this.cachedPosX;
    const zs = this.cachedPosZ;
    const offset = this.params.offset.value;
    const n = Math.min(xs.length, zs.length);
    const ys = new Array(n);

    for (let i = 0; i < n; i++) {
      ys[i] = sampleHeightmap(hm, xs[i], zs[i]) + offset;
    }

    this.internals.posY.set(ys);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const heightSamplerNodeDef: NodeDef<HeightSamplerNode> = {
  type: 'HeightSampler',
  shortName: 'heightsample',
  info: 'Samples terrain height at (x, z) positions using bilinear interpolation. Place objects on terrain.',
  domain: Domain.Data,
  endpoints: heightSamplerEndpoints,
  ctor: HeightSamplerNode,
};
